import ClientPanel from "@/data/models/ClientPanel";
import { IresDevice, isIdle } from "@/data/models/IresDevice";

/**
 * One row of the hyDry device list: a device plus the display state the row derives
 * from it.
 *
 * Lives here rather than in `IresDeviceList.vue` so the row components do not have
 * to import their own parent, and so the reconciliation below is testable.
 */
export class IresDeviceItemData {
  iresDevice: IresDevice;
  isIdle: boolean;
  serialNumber: string;

  lastDisplayedDate!: Date;

  timeSinceLastUpdateInSeconds = 0;
  matchingClientPanel: ClientPanel | undefined = undefined;

  constructor(iresDevice: IresDevice, lastDisplayedDate: Date) {
    this.serialNumber = iresDevice.serialNo;
    this.iresDevice = iresDevice;
    this.isIdle = isIdle(iresDevice);
    this.lastDisplayedDate = lastDisplayedDate;
  }

  updateDate(date: Date) {
    this.lastDisplayedDate = date;
  }

  /**
   * Points this item at a newer version of the same device.
   *
   * Keeps the display state (`lastDisplayedDate`, `matchingClientPanel`) so a row
   * does not lose its "last update" text or its client-panel join while a snapshot
   * is applied.
   */
  updateIresDevice(iresDevice: IresDevice) {
    this.iresDevice = iresDevice;
    this.isIdle = isIdle(iresDevice);
  }

  get assignedProjectId(): string | undefined | null {
    return (
      this.iresDevice.currentAssignment?.currentProjectId ??
      this.matchingClientPanel?.currentProjectId
    );
  }
}

/**
 * Applies a device snapshot to a list of row items, reusing the item instance of
 * every device that is still present and dropping the ones that are gone.
 *
 * Rebuilding all items on every snapshot handed each rendered row a new
 * `iresDeviceItem` prop identity, so one status write from any member client
 * re-rendered and recomputed every visible row. Devices report status constantly,
 * which makes that the steady state rather than a burst, so the list's 100 ms
 * snapshot debounce cannot help with it.
 *
 * An item is only pointed at a new device object when the object actually changed:
 * the member-client observers re-deserialize only the docs of the member whose
 * snapshot fired, so devices of the other member clients keep their identity and
 * their rows stay untouched.
 *
 * `itemsBySerialNumber` is the caller's index and is updated in place.
 */
export function reconcileIresDeviceItems(
  devices: Array<IresDevice>,
  itemsBySerialNumber: Map<string, IresDeviceItemData>,
  now: Date
): Array<IresDeviceItemData> {
  const items: Array<IresDeviceItemData> = [];
  const presentSerialNumbers = new Set<string>();

  devices.forEach((iresDevice) => {
    const serialNumber = iresDevice.serialNo;
    let item = itemsBySerialNumber.get(serialNumber);

    if (!item) {
      item = new IresDeviceItemData(iresDevice, now);
      itemsBySerialNumber.set(serialNumber, item);
    } else if (item.iresDevice !== iresDevice) {
      item.updateIresDevice(iresDevice);
    }

    presentSerialNumbers.add(serialNumber);
    items.push(item);
  });

  itemsBySerialNumber.forEach((_, serialNumber) => {
    if (!presentSerialNumbers.has(serialNumber)) {
      itemsBySerialNumber.delete(serialNumber);
    }
  });

  return items;
}
